import React, { useState } from 'react';
import { useApp } from '../AppContext';
import { AppointmentStatus, formatDate } from '../types';
import WeeklyEditor from './dashboard/WeeklyEditor';
import OverridesEditor from './dashboard/OverridesEditor';
import EmailTemplates from './dashboard/EmailTemplates';
import AccountSettings from './dashboard/AccountSettings';

type Tab = 'appointments' | 'schedule' | 'emails' | 'account';

function StatusBadge({ status }: { status: AppointmentStatus }) {
  const map: Record<AppointmentStatus, string> = {
    'In Attesa': 'bg-amber-400/25 text-amber-300',
    'Confermato': 'bg-emerald-400/25 text-emerald-300',
    'Completato': 'bg-blue-400/25 text-blue-300',
    'Rifiutato': 'bg-rose-400/25 text-rose-300',
  };
  return <span className={`${map[status]} px-2.5 py-1 rounded-full text-xs font-semibold`}>{status}</span>;
}

export default function Dashboard() {
  const {
    dashboardOpen, closeDashboard, logout,
    appointments, changeAppointmentStatus, showToast,
  } = useApp();
  const [tab, setTab] = useState<Tab>('appointments');

  if (!dashboardOpen) return null;

  const handleStatus = async (id: number, client: string, status: AppointmentStatus) => {
    await changeAppointmentStatus(id, status);
    showToast(`L'appuntamento di ${client} è stato contrassegnato come: ${status}`);
  };

  return (
    <section id="dashboard-section" className="min-h-screen py-10 bg-forestDark text-cream relative">
      <div className="absolute top-4 right-6 flex items-center gap-2 z-10">
        <button onClick={logout} className="text-xs uppercase tracking-widest text-sage hover:text-cream bg-forest/60 px-4 py-2 rounded-full smooth-transition">Logout</button>
        <button onClick={closeDashboard} className="text-xs uppercase tracking-widest text-sage hover:text-cream bg-forest px-4 py-2 rounded-full smooth-transition">← Torna al sito</button>
      </div>

      <div className="max-w-7xl mx-auto px-6 space-y-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between border-b border-sage/20 pb-6 gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-sage font-semibold">Pannello di Controllo Clinico</span>
            <h2 className="font-serif text-3xl md:text-4xl">Studio Medico Dr. Balducci</h2>
          </div>
          <div className="flex gap-2 border border-sage/20 rounded-full p-1 flex-wrap">
            <TabBtn active={tab === 'appointments'} onClick={() => setTab('appointments')}>Appuntamenti</TabBtn>
            <TabBtn active={tab === 'schedule'} onClick={() => setTab('schedule')}>Orari & Date</TabBtn>
            <TabBtn active={tab === 'emails'} onClick={() => setTab('emails')}>Notifiche email</TabBtn>
            <TabBtn active={tab === 'account'} onClick={() => setTab('account')}>Account</TabBtn>
          </div>
        </div>

        {tab === 'appointments' && (
          <div className="bg-forest p-6 rounded-3xl border border-sage/15 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl">Ultimi Appuntamenti Prenotati</h3>
              <span className="text-xs text-sage">I nuovi appuntamenti appariranno qui all'istante</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-sage/10 text-xs uppercase tracking-wider text-sage">
                    <th className="pb-3">Cliente</th>
                    <th className="pb-3">Servizio</th>
                    <th className="pb-3">Data e Ora</th>
                    <th className="pb-3">Stato</th>
                    <th className="pb-3 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.length === 0 && (
                    <tr><td colSpan={5} className="py-6 text-center text-xs text-sage italic">Nessuna prenotazione presente.</td></tr>
                  )}
                  {appointments.map((app, i) => (
                    <tr key={app.id} className="row-anim border-b border-sage/10 hover:bg-forest/20 smooth-transition" style={{ ['--d' as any]: `${i * 60}ms` }}>
                      <td className="py-4">
                        <div className="font-bold text-cream">{app.client_name}</div>
                        <div className="text-[11px] text-sage">{app.phone} | {app.email}</div>
                      </td>
                      <td className="py-4 text-sm">{app.service_name}</td>
                      <td className="py-4 text-sm">
                        <div>{app.weekday_name} {formatDate(app.date)}</div>
                        <div className="text-[11px] font-bold text-sage">{app.time}</div>
                      </td>
                      <td className="py-4"><StatusBadge status={app.status} /></td>
                      <td className="py-4 text-right space-x-1 whitespace-nowrap">
                        {app.status === 'In Attesa' && (
                          <>
                            <button onClick={() => handleStatus(app.id, app.client_name, 'Confermato')} className="bg-emerald-800 text-cream px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-emerald-700 smooth-transition">Accetta</button>
                            <button onClick={() => handleStatus(app.id, app.client_name, 'Rifiutato')} className="bg-rose-900 text-cream px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-rose-800 smooth-transition">Rifiuta</button>
                          </>
                        )}
                        {app.status === 'Confermato' && (
                          <button onClick={() => handleStatus(app.id, app.client_name, 'Completato')} className="bg-blue-800 text-cream px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-blue-700 smooth-transition">Concludi</button>
                        )}
                        <button onClick={() => showToast(`Note di ${app.client_name}: "${app.notes}"`)} className="bg-sage/20 text-cream px-2.5 py-1.5 rounded-lg text-xs hover:bg-sage/40 smooth-transition" title="Leggi note client">Note</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'schedule' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <WeeklyEditor />
            <OverridesEditor />
          </div>
        )}

        {tab === 'emails' && <EmailTemplates />}

        {tab === 'account' && <AccountSettings />}
      </div>
    </section>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-widest font-bold smooth-transition ${
        active ? 'bg-sage text-forestDark' : 'text-sage hover:text-cream'
      }`}
    >
      {children}
    </button>
  );
}
